import './Workflow.css';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { immutableDelete, immutableInsert, immutablePush, immutableSet } from '@icp/utils';
import { get } from 'lodash-es';
import { CodeEditor, Resizable } from '@icp/components';
import NodeRenderer from './NodeRenderer';
import { NodeContextProvider } from './NodeContextProvider';
import { checkValueId, extractEdges, handleSize, spacerSize } from './utils';
import NodeSettings from './NodeSettings/NodeSettings';
import EdgeRenderer from './EdgeRenderer';

const defaultValue = { nodeList: [] };

const Workflow = forwardRef(function Workflow(props, ref) {
  const {
    id,
    className,
    style,
    readonly,
    workflowTypeNodeList,
    entity,
    value: valueProp,
    onChange,
    ...other
  } = props;

  const debugMode = new URLSearchParams(window.location.search).get('debug');

  const value = useMemo(() => {
    return checkValueId(valueProp || defaultValue);
  }, [valueProp]);

  const edges = useMemo(() => {
    return extractEdges(value);
  }, [value]);

  const [selectedKeyPath, setSelectedKeyPath] = useState(null);

  const canvasRef = useRef(null);

  const workflowTypeNodeMap = useMemo(() => {
    const map = {};
    workflowTypeNodeList.forEach((node) => {
      map[node.token] = node;
    });
    return map;
  }, [workflowTypeNodeList]);

  const editorRef = useRef(null);
  useEffect(() => {
    editorRef.current?.setValue(JSON.stringify(value, null, 2));
  }, [value]);

  const handleJSONChange = () => {
    const newValue = JSON.parse(editorRef.current.getValue());
    onChange(newValue);
  };

  const actions = useMemo(() => {
    return {
      selectNode: (keyPath) => {
        setSelectedKeyPath(keyPath);
      },
      addNode: (keyPath, newNode) => {
        const newValue = immutableInsert(value, keyPath, newNode);
        onChange(checkValueId(newValue));
      },
      addBranch: (keyPath, newBranch) => {
        const newValue = immutablePush(value, keyPath.concat('branches'), newBranch);
        onChange(checkValueId(newValue));
      },
      deleteNode: (keyPath) => {
        const newValue = immutableDelete(value, keyPath);
        onChange(newValue);
        if (String(selectedKeyPath).startsWith(String(keyPath))) {
          setSelectedKeyPath(null);
        }
      },
      deleteBranch: (keyPath) => {
        const parentKeyPath = keyPath.slice(0, -2);
        const parentNode = get(value, parentKeyPath);
        const branches = parentNode.branches;

        // 条件节点必须保留至少两个分支
        let deleteKeyPath = keyPath;
        if (branches.length <= 2) {
          deleteKeyPath = parentKeyPath;
        }

        const newValue = immutableDelete(value, deleteKeyPath);
        onChange(newValue);

        if (String(selectedKeyPath).startsWith(String(deleteKeyPath))) {
          setSelectedKeyPath(null);
        }
      },
      updateNode: (propertyKeyPath, newNode) => {
        const newValue = immutableSet(value, selectedKeyPath.concat(propertyKeyPath), newNode);
        onChange(checkValueId(newValue));
      },
    };
  }, [onChange, selectedKeyPath, value]);

  const events = useMemo(() => {
    return {
      onSelectNode: actions.selectNode,
      onAddNode: actions.addNode,
      onAddBranch: actions.addBranch,
      onDeleteNode: actions.deleteNode,
      onDeleteBranch: actions.deleteBranch,
      onUpdateNode: actions.updateNode,
    };
  }, [actions]);

  if (!(Array.isArray(workflowTypeNodeList) && workflowTypeNodeList.length) || !entity) {
    return null;
  }

  return (
    <NodeContextProvider
      events={events}
      workflowTypeNodeMap={workflowTypeNodeMap}
      entity={entity}
      value={value}
      selectedKeyPath={selectedKeyPath}
      readonly={readonly}
      canvasRef={canvasRef}
    >
      <div
        className={clsx('icp-workflow', className)}
        {...other}
        style={{
          '--spacer-size': `${spacerSize}px`,
          '--handle-size': `${handleSize}px`,
          ...style,
        }}
        ref={ref}
      >
        {debugMode === 'true' ? (
          <Resizable min={240} placement="right" isPercent={false}>
            <div style={{ width: 400 }}>
              <CodeEditor
                language="json"
                ref={editorRef}
                style={{ width: '100%', borderRight: '1px solid var(--border-color)' }}
                onBlur={handleJSONChange}
              >
                {JSON.stringify(value, null, 2)}
              </CodeEditor>
            </div>
          </Resizable>
        ) : null}
        <div
          className="icp-workflow-canvas icp-thin-scrollbar"
          onClick={() => setSelectedKeyPath(null)}
          ref={canvasRef}
        >
          <div className="icp-workflow-canvas-size-wrapper">
            <EdgeRenderer id={id} edges={edges} />
            <div className="icp-workflow-nodes">
              <NodeRenderer keyPath={['nodeList']} nodeList={value.nodeList} />
            </div>
          </div>
        </div>
        <NodeSettings onClose={() => setSelectedKeyPath(null)} />
      </div>
    </NodeContextProvider>
  );
});

Workflow.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  readonly: PropTypes.bool,
  workflowTypeNodeList: PropTypes.arrayOf(PropTypes.shape({})),
  entity: PropTypes.shape({
    name: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  value: PropTypes.shape({
    nodeList: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onChange: PropTypes.func,
};

export default Workflow;
